import React, { useEffect, useMemo } from "react";

import { Label } from "@packages/ds";
import { FormattedMessage } from "react-intl";

import { Link } from "ds/Button";
import Select from "ds/Select";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { useLoadInvitationsAndAccesses } from "Reducers/access/access.hooks";
import { selectMembers } from "Reducers/organization/members/membersList.selectors";
import { loadAllMembers } from "Reducers/organization/members/thunks/loadAllMembers.thunk";
import { sortedUserAccessAndInvitationsByProjectIdSelector } from "Reducers/userAccess/userAccess.selectors";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import * as S from "./OrganizationMemberSelect.styles";

import type { Organization } from "@packages/client";

type Props = {
  organization: Organization;
  value?: string;
  onChange: (value?: string) => void;
};

export const OrganizationMemberSelect = (props: Props) => {
  const dispatch = useAppDispatch();
  const members = useAppSelector(selectMembers);

  useLoadInvitationsAndAccesses();

  const sortedAccesses = useSelectorWithUrlParams(
    sortedUserAccessAndInvitationsByProjectIdSelector,
    { key: "email", direction: "asc" }
  );

  useEffect(() => {
    dispatch(loadAllMembers({ organizationId: props.organization.id }));
  }, [dispatch, props.organization.id]);

  const filteredMembers = useMemo(
    () =>
      members
        .filter(({ member, user }) => {
          const isAlreadySelected = props.value === member.id;
          const isAlreadyMember = sortedAccesses.some(
            existingMember => existingMember.email === user?.email
          );
          return !isAlreadyMember && !isAlreadySelected;
        })
        ?.map(({ user }) => ({
          value: user?.email || "",
          label:
            user?.first_name || user?.last_name
              ? `${user?.first_name} ${user?.last_name}`
              : user?.username || user?.email || ""
        })),
    [props.value, members, sortedAccesses]
  );

  const selectedValue = useMemo(() => {
    const selected = members.find(({ member }) => member.id === props.value);
    if (!selected) return undefined;

    return {
      value: selected?.member.id,
      label: `${selected?.user?.first_name} ${selected?.user?.last_name}`
    };
  }, [members, props.value]);

  return (
    <>
      <Label required htmlFor="organizationMemberSelect">
        <FormattedMessage id="organization.project.access.invitemember.member" />
      </Label>
      <S.SelectWrapper>
        <Select
          inputId="organizationMemberSelect"
          value={selectedValue}
          isMulti={false}
          id="organization.project.access.searchUser"
          placeholder={
            <FormattedMessage id="organization.project.access.searchUser" />
          }
          options={filteredMembers}
          onChange={value => {
            props.onChange(value?.value);
          }}
        />
      </S.SelectWrapper>
      <S.Notice>
        <FormattedMessage
          id="organization.project.access.invitemember"
          values={{
            // TODO this should use the organization label
            a: (...chunk) => (
              <Link
                analyticId="settings_users"
                to={`/${props.organization.name}/-/settings/users`}
              >
                {chunk}
              </Link>
            )
          }}
        />
      </S.Notice>
    </>
  );
};
